import { Col, Row, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import Flex from 'components/common/Flex';
import { FormProvider, RHFCheck, RHFControl } from 'components/common/form';
import paths from 'routes/paths';
import { useForm } from 'react-hook-form';
import AuthContext from 'context/authContext';
import { useContext, useState } from 'react';
import LoadingButton from 'components/common/LoadingButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { authHelpers } from 'lib/supabase';

const credentialsSchema = Yup.object().shape({
  email: Yup.string().email().required('Required Field'),
  password: Yup.string().required('Required Field')
});

const mfaSchema = Yup.object().shape({
  code: Yup.string()
    .required('Required Field')
    .length(6, 'Code must be 6 digits')
});

// Steps: 1 = credentials, 2 = MFA verify, 3 = MFA setup
const LoginForm = () => {
  const { loginWithMfaCheck, verifyMfaLogin } = useContext(AuthContext);
  const [step, setStep] = useState(1);
  const [factorId, setFactorId] = useState(null);
  const [loading, setLoading] = useState(false);

  // MFA Setup state
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [setupCode, setSetupCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Step 1: Credentials form
  const credentialsMethods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      password: '',
      email: '',
      remember: false
    },
    resolver: yupResolver(credentialsSchema)
  });

  // Step 2: MFA form
  const mfaMethods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { code: '' },
    resolver: yupResolver(mfaSchema)
  });

  const { handleSubmit: handleCredentialsSubmit, watch: watchCredentials } =
    credentialsMethods;
  const { email, password } = watchCredentials();

  const { handleSubmit: handleMfaSubmit, watch: watchMfa } = mfaMethods;
  const { code } = watchMfa();

  // Step 1 handler: verify credentials, check if MFA required or setup needed
  const onCredentialsSubmit = async ({ email, password }) => {
    setLoading(true);
    const { success, error, mfaRequired, mfaSetupRequired, factorId } =
      await loginWithMfaCheck({ email, password });
    setLoading(false);

    if (!success) {
      toast.error(error || 'Invalid credentials');
      return;
    }

    if (mfaRequired) {
      setFactorId(factorId);
      setStep(2);
    } else if (mfaSetupRequired) {
      // Start MFA enrollment
      await startMfaSetup();
    }
    // If neither, loginWithMfaCheck handles navigation
  };

  // Start MFA setup - enroll and show QR code
  const startMfaSetup = async () => {
    setLoading(true);
    try {
      const { data, error } = await authHelpers.enrollMfa();

      if (error) {
        toast.error(error.message || 'Failed to set up two-factor authentication');
        setLoading(false);
        return;
      }

      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
      setFactorId(data.id);
      setStep(3);
    } catch (error) {
      console.error('MFA setup error:', error);
      toast.error('Failed to set up two-factor authentication');
    }
    setLoading(false);
  };

  // Step 2 handler: verify TOTP code (existing MFA)
  const onMfaSubmit = async ({ code }) => {
    setLoading(true);
    const { success, error } = await verifyMfaLogin({
      factorId,
      code
    });
    setLoading(false);

    if (!success) {
      toast.error(error || 'Invalid verification code');
    }
  };

  // Step 3 handler: verify and activate MFA setup
  const handleSetupVerify = async () => {
    if (setupCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setVerifying(true);
    try {
      const { error } = await authHelpers.challengeAndVerifyMfa({
        factorId,
        code: setupCode
      });

      if (error) {
        toast.error(error.message || 'Invalid verification code');
        setVerifying(false);
        return;
      }

      toast.success('Two-factor authentication enabled!');

      // Now complete the login
      const { success, error: loginError } = await verifyMfaLogin({
        factorId,
        code: setupCode
      });

      if (!success) {
        toast.error(loginError || 'Login failed');
      }
    } catch (error) {
      console.error('MFA verify error:', error);
      toast.error('Failed to verify code');
    }
    setVerifying(false);
  };

  // Back to step 1
  const handleBack = () => {
    setStep(1);
    setFactorId(null);
    setQrCode(null);
    setSecret(null);
    setSetupCode('');
    mfaMethods.reset();
  };

  // Step 3: MFA Setup (QR code)
  if (step === 3) {
    return (
      <>
        <div className="mb-2">
          <h5>Set up two-factor authentication</h5>
        </div>
        <Flex direction="column" gap={3} alignItems="center" className="mt-3">
          <FontAwesomeIcon icon="lock" className="text-primary" size="3x" />
          <p className="text-center mb-0">
            Scan this QR code with your authenticator app
            <br />
            <span className="text-muted fs--1">
              (Google Authenticator, Authy, etc.)
            </span>
          </p>
          <img src={qrCode} alt="MFA QR Code" style={{ maxWidth: 180 }} />
          <p className="text-muted fs--1 text-center mb-0">
            Or enter manually: <code className="user-select-all">{secret}</code>
          </p>
          <Form.Group className="w-100">
            <Form.Label>Enter the 6-digit code from your app</Form.Label>
            <Form.Control
              type="text"
              placeholder="000000"
              maxLength={6}
              value={setupCode}
              onChange={e => setSetupCode(e.target.value.replace(/\D/g, ''))}
              autoComplete="one-time-code"
              inputMode="numeric"
            />
          </Form.Group>
          <LoadingButton
            className="w-100"
            onClick={handleSetupVerify}
            loading={verifying}
            disabled={setupCode.length !== 6}
          >
            Verify & Continue
          </LoadingButton>
        </Flex>
      </>
    );
  }

  // Step 2: MFA Verify (existing MFA)
  if (step === 2) {
    return (
      <>
        <div className="mb-2">
          <h5>Two-factor authentication</h5>
        </div>
        <FormProvider
          methods={mfaMethods}
          onSubmit={handleMfaSubmit(onMfaSubmit)}
        >
          <Flex
            gap={3}
            direction="column"
            alignItems="center"
            className="mt-3"
          >
            <FontAwesomeIcon icon="lock" className="text-primary" size="3x" />
            <p className="text-center mb-0">
              Enter the 6-digit code from your
              <br />
              authenticator app
            </p>
            <RHFControl
              name="code"
              type="text"
              placeholder="Enter 6-digit code"
              maxLength={6}
              autoComplete="one-time-code"
              inputMode="numeric"
            />
            <LoadingButton
              type="submit"
              color="primary"
              className="w-100"
              disabled={!code || code.length < 6}
              loading={loading}
            >
              Verify
            </LoadingButton>
            <a
              href="#!"
              className="fs--1"
              onClick={e => {
                e.preventDefault();
                handleBack();
              }}
            >
              Back to login
            </a>
          </Flex>
        </FormProvider>
      </>
    );
  }

  // Step 1: Credentials
  return (
    <>
      <div className="mb-2">
        <h5>Log in</h5>
      </div>
      <FormProvider
        methods={credentialsMethods}
        onSubmit={handleCredentialsSubmit(onCredentialsSubmit)}
      >
        <Flex gap={3} direction="column" className="mt-3">
          <RHFControl name="email" type="email" placeholder="Email address" />
          <RHFControl name="password" type="password" placeholder="Password" />

          <Row className="justify-content-between align-items-center">
            <Col xs="auto">
              <RHFCheck name="remember" label="Remember me" type="checkbox" />
            </Col>

            <Col xs="auto">
              <Link className="fs--1 mb-0" to={paths.forgotPassword}>
                Forgot Password?
              </Link>
            </Col>
          </Row>

          <LoadingButton
            type="submit"
            color="primary"
            className="w-100"
            disabled={!email || !password}
            loading={loading}
          >
            Log in
          </LoadingButton>
        </Flex>
      </FormProvider>
    </>
  );
};

export default LoginForm;
